import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { GoogleAdsNotice } from '@/components/google-ads/GoogleAdsShell';
import { errMsg } from '@/components/google-ads/youtube/youtubeShared';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import {
  googleAdsService,
  GoogleAdsAccount,
  GoogleAdsLeadFormDelivery,
  GoogleAdsLeadWebhookSetup,
  GoogleAdsLeadFormSummary,
  GoogleAdsLeadFormFieldType,
  GoogleAdsLeadFormCtaType,
  GoogleAdsLeadFormCreatePayload,
  GoogleAdsCampaignSummary,
} from '@/services/google-ads.service';
import {
  FormInput, Plus, ExternalLink, Globe, Mail, Phone, User, MapPin, Briefcase, Link as LinkIcon,
  CheckCircle2, AlertTriangle, Copy, Eye, EyeOff, Webhook, FlaskConical, Inbox,
} from 'lucide-react';

const CTA_OPTIONS: Array<{ value: GoogleAdsLeadFormCtaType; label: string }> = [
  { value: 'LEARN_MORE',  label: 'Learn more' },
  { value: 'GET_QUOTE',   label: 'Get quote' },
  { value: 'APPLY_NOW',   label: 'Apply now' },
  { value: 'SIGN_UP',     label: 'Sign up' },
  { value: 'CONTACT_US',  label: 'Contact us' },
  { value: 'SUBSCRIBE',   label: 'Subscribe' },
  { value: 'DOWNLOAD',    label: 'Download' },
  { value: 'BOOK_NOW',    label: 'Book now' },
  { value: 'GET_OFFER',   label: 'Get offer' },
];

const FIELD_OPTIONS: Array<{ value: GoogleAdsLeadFormFieldType; label: string; icon: React.ReactNode }> = [
  { value: 'EMAIL',          label: 'Email',          icon: <Mail size={12} /> },
  { value: 'PHONE_NUMBER',   label: 'Phone',          icon: <Phone size={12} /> },
  { value: 'FULL_NAME',      label: 'Full name',      icon: <User size={12} /> },
  { value: 'FIRST_NAME',     label: 'First name',     icon: <User size={12} /> },
  { value: 'LAST_NAME',      label: 'Last name',      icon: <User size={12} /> },
  { value: 'POSTAL_CODE',    label: 'Postal code',    icon: <MapPin size={12} /> },
  { value: 'STREET_ADDRESS', label: 'Street address', icon: <MapPin size={12} /> },
  { value: 'CITY',           label: 'City',           icon: <MapPin size={12} /> },
  { value: 'REGION',         label: 'Region',         icon: <MapPin size={12} /> },
  { value: 'COUNTRY',        label: 'Country',        icon: <MapPin size={12} /> },
  { value: 'COMPANY_NAME',   label: 'Company',        icon: <Briefcase size={12} /> },
  { value: 'JOB_TITLE',      label: 'Job title',      icon: <Briefcase size={12} /> },
  { value: 'WORK_EMAIL',     label: 'Work email',     icon: <Briefcase size={12} /> },
  { value: 'WORK_PHONE',     label: 'Work phone',     icon: <Briefcase size={12} /> },
];

interface FormState {
  name: string;
  businessName: string;
  callToActionType: GoogleAdsLeadFormCtaType;
  callToActionDescription: string;
  headline: string;
  description: string;
  privacyPolicyUrl: string;
  fields: GoogleAdsLeadFormFieldType[];
  postSubmitHeadline: string;
  postSubmitDescription: string;
  postSubmitCallToActionText: string;
}

function defaultFormState(): FormState {
  return {
    name: '',
    businessName: '',
    callToActionType: 'LEARN_MORE',
    callToActionDescription: '',
    headline: '',
    description: '',
    privacyPolicyUrl: '',
    fields: ['EMAIL', 'PHONE_NUMBER', 'FULL_NAME'],
    postSubmitHeadline: 'Thanks!',
    postSubmitDescription: 'We\'ll be in touch shortly.',
    postSubmitCallToActionText: 'Visit our site',
  };
}

const DELIVERY: Record<GoogleAdsLeadFormDelivery, { label: string; hint: string; tone: string }> = {
  connected:         { label: 'Delivering to Slightor', hint: 'Submissions arrive here as leads.', tone: 'bg-success-soft text-success' },
  key_mismatch:      { label: 'Key mismatch', hint: 'The form uses this account\'s URL with a different key — paste the key shown above.', tone: 'bg-danger-soft text-danger' },
  legacy_url:        { label: 'Old webhook URL', hint: 'Slightor refuses the old shared URL. Replace it with this account\'s URL and key.', tone: 'bg-warning-soft text-warning' },
  other_destination: { label: 'Sends elsewhere', hint: 'This form posts to another webhook, so Slightor does not receive its leads.', tone: 'bg-warning-soft text-warning' },
  no_webhook:        { label: 'No webhook', hint: 'Leads stay in Google Ads until a webhook is added.', tone: 'bg-surface-alt text-text-muted' },
  unknown:           { label: 'Delivery not verified', hint: 'Google did not return this form\'s delivery settings.', tone: 'bg-surface-alt text-text-muted' },
};

function ago(iso?: string): string {
  if (!iso) return 'Never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  return new Date(iso).toLocaleDateString();
}

function CopyRow({ label, value, secret = false }: { label: string; value: string; secret?: boolean }) {
  const [shown, setShown] = useState(!secret);
  const copy = () => navigator.clipboard.writeText(value).then(() => toast.success(`${label} copied`), () => toast.error('Copy failed'));
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="w-24 shrink-0 text-2xs font-semibold uppercase tracking-wide text-text-muted">{label}</span>
      <code className="min-w-0 flex-1 truncate rounded-md bg-surface-alt px-2 py-1 text-xs text-text-primary">{shown ? value : '•'.repeat(24)}</code>
      {secret && (
        <button type="button" onClick={() => setShown(v => !v)} className="rounded-md p-1 text-text-muted hover:bg-surface-alt" aria-label={shown ? 'Hide key' : 'Show key'}>
          {shown ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      )}
      <button type="button" onClick={copy} className="rounded-md p-1 text-text-muted hover:bg-surface-alt" aria-label={`Copy ${label}`}><Copy size={13} /></button>
    </div>
  );
}

type Props = {
  /** Rendered inside a TabbedPage shell — suppress this page's own Topbar. */
  embedded?: boolean;
};

export default function GoogleAdsLeadForms({ embedded = false }: Props = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [accounts, setAccounts] = useState<GoogleAdsAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const [accountUid, setAccountUid] = useState<string>('');
  const [forms, setForms] = useState<GoogleAdsLeadFormSummary[] | null>(null);
  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>(defaultFormState());
  const [busy, setBusy] = useState(false);

  // Attach modal
  const [attachTarget, setAttachTarget] = useState<GoogleAdsLeadFormSummary | null>(null);
  const [attachCampaignId, setAttachCampaignId] = useState('');
  const [attaching, setAttaching] = useState(false);

  const [webhook, setWebhook] = useState<GoogleAdsLeadWebhookSetup | null>(null);

  useEffect(() => {
    googleAdsService.list()
      .then((accs) => {
        accs = accs.filter(a => a.customerId && !a.isManager && a.isActive);
        setAccounts(accs);
        const def = accs.find((a) => a.isDefault) ?? accs[0];
        if (def) setAccountUid(def.accountUid);
      })
      .catch(e => setLoadError(errMsg(e)))
      .finally(() => setAccountsLoading(false));
  }, []);

  const refresh = async (uid: string) => {
    if (!uid) return;
    const version = ++loadVersion.current;
    setLoadError(null);
    setForms(null);
    setWebhook(null);
    if (isAdmin) {
      googleAdsService.getLeadWebhookSetup(uid)
        .then(setup => { if (version === loadVersion.current) setWebhook(setup); })
        .catch(() => undefined);
    }
    try {
      const [list, camps] = await Promise.all([
        googleAdsService.listLeadForms(uid),
        googleAdsService.listCampaigns(uid).catch(() => []),
      ]);
      if (version === loadVersion.current) { setForms(list); setCampaigns(camps); }
    } catch (e) {
      if (version === loadVersion.current) setLoadError(errMsg(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (accountUid) refresh(accountUid); }, [accountUid]);

  const toggleField = (f: GoogleAdsLeadFormFieldType) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.includes(f) ? prev.fields.filter((x) => x !== f) : [...prev.fields, f],
    }));
  };

  const create = async () => {
    if (!accountUid) return;
    if (!form.name.trim()) { toast.error('Internal name required'); return; }
    if (!form.businessName.trim()) { toast.error('Business name required'); return; }
    if (!form.headline.trim()) { toast.error('Headline required'); return; }
    if (!form.description.trim()) { toast.error('Description required'); return; }
    if (!form.privacyPolicyUrl.trim()) { toast.error('Privacy policy URL required'); return; }
    if (form.fields.length === 0) { toast.error('Pick at least one field'); return; }

    setBusy(true);
    try {
      const payload: GoogleAdsLeadFormCreatePayload = {
        name: form.name.trim(),
        businessName: form.businessName.trim(),
        callToActionType: form.callToActionType,
        callToActionDescription: form.callToActionDescription.trim() || undefined,
        headline: form.headline.trim(),
        description: form.description.trim(),
        privacyPolicyUrl: form.privacyPolicyUrl.trim(),
        fields: form.fields,
        postSubmitHeadline: form.postSubmitHeadline.trim(),
        postSubmitDescription: form.postSubmitDescription.trim(),
        postSubmitCallToActionText: form.postSubmitCallToActionText.trim() || undefined,
      };
      const r = await googleAdsService.createLeadForm(accountUid, payload);
      toast.success(`Lead form created · ID ${r.id}. Attach it to a campaign to start collecting leads.`);
      setCreateOpen(false);
      setForm(defaultFormState());
      await refresh(accountUid);
    } catch {
      // axios interceptor toasts
    } finally { setBusy(false); }
  };

  const attach = async () => {
    if (!accountUid || !attachTarget || !attachCampaignId) return;
    setAttaching(true);
    try {
      await googleAdsService.attachLeadForm(accountUid, attachTarget.id, { campaignId: attachCampaignId });
      toast.success(`Form attached. Submissions will arrive at the configured webhook.`);
      setAttachTarget(null);
      setAttachCampaignId('');
    } catch {
      // axios interceptor toasts
    } finally { setAttaching(false); }
  };

  if (accountsLoading) return <div className="skeleton h-52 rounded-2xl" aria-label="Loading accounts" />;
  if (loadError && !accounts.length) return <GoogleAdsNotice title="Unable to load accounts" error>{loadError}</GoogleAdsNotice>;
  if (accounts.length === 0) {
    return (
      <>
        {!embedded && <Topbar title="Google Ads Lead Forms" breadcrumb="Google Ads" />}
        <div className={clsx('gads-embedded w-full px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
          <EmptyState
            icon={<Globe size={40} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Lead forms attach to campaigns. Connect Google Ads, attach a customer, then come back."
            action={
              <a href="/google-ads/account">
                <Button variant="primary"><ExternalLink size={14} /> Go to Google Ads Account</Button>
              </a>
            }
          />
        </div>
      </>
    );
  }

  // Account switcher and per-page actions. The tabbed shell hides this
  // page's Topbar, so the same controls render above the content there.
  const headerActions = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {accounts.length > 1 && (
          <Select aria-label="Advertising account" value={accountUid} onChange={(e) => setAccountUid(e.target.value)} className="!h-9 !text-xs">
            {accounts.map((a) => (
              <option key={a.accountUid} value={a.accountUid}>
                {a.customerName ?? a.googleEmail} {a.customerId ? `(${a.customerId})` : ''}
              </option>
            ))}
          </Select>
        )}
        <Button variant="primary" className="gads-primary" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> New lead form
        </Button>
      </div>
  );

  return (
    <>
      {!embedded && (
        <Topbar
        title="Google Ads Lead Forms"
        breadcrumb="Google Ads"
        description="Create lead-form assets and attach them to campaigns. Submissions are webhooked back to Slightor and become leads automatically."
        right={headerActions}
        />
      )}

      <div className={clsx('gads-embedded w-full px-7 space-y-4', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {embedded && <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><div><h3 className="gads-panel-title">Lead forms</h3><p className="gads-muted mt-1">Capture customer interest directly from your Google ads.</p></div>{headerActions}</div>}
        {loadError && <GoogleAdsNotice title="Unable to load lead forms" error>{loadError} <button className="ml-2 underline" onClick={() => refresh(accountUid)}>Try again</button></GoogleAdsNotice>}
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Webhook size={16} /></div>
              <div>
                <div className="text-sm font-semibold text-text-primary">Lead delivery</div>
                <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-text-muted">
                  Forms created here post each submission to this account's own webhook with an account key; Slightor then creates the Contact and Lead.
                  Google retries failed deliveries, and duplicates are ignored.
                </p>
              </div>
            </div>
            {webhook && (
              <div className="flex flex-wrap gap-2 text-2xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2.5 py-1 text-text-secondary"><Inbox size={11} className="text-success" /> Last lead · {ago(webhook.lastLeadAt)}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2.5 py-1 text-text-secondary"><FlaskConical size={11} className="text-info" /> Last test · {ago(webhook.lastTestAt)}</span>
              </div>
            )}
          </div>
          {webhook && webhook.lastErrorAt && (!webhook.lastLeadAt || webhook.lastErrorAt > webhook.lastLeadAt) && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{webhook.lastError} <span className="text-text-muted">({ago(webhook.lastErrorAt)})</span></span>
            </div>
          )}
          {webhook?.problems.map(problem => (
            <div key={problem} className="mt-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /><span>{problem}</span>
            </div>
          ))}
          {isAdmin ? (
            webhook?.url && webhook.key ? (
              <div className="mt-4 space-y-2 border-t border-border pt-3">
                <CopyRow label="Webhook URL" value={webhook.url} />
                <CopyRow label="Key" value={webhook.key} secret />
                <p className="text-2xs leading-relaxed text-text-muted">
                  For a form built in Google Ads: open the form → Lead delivery → Webhook integration, paste this URL and key, then press <strong>Send test data</strong>. The test shows here as "Last test". Keep the key private — it lets Google add leads to this account.
                </p>
              </div>
            ) : null
          ) : (
            <p className="mt-3 text-2xs text-text-muted">A company admin can view this account's webhook URL and key for forms built directly in Google Ads.</p>
          )}
        </Card>

        {forms === null && !loadError && (
          <Card><div className="skeleton h-24 rounded-lg" /></Card>
        )}

        {forms && forms.length === 0 && (
          <EmptyState
            icon={<FormInput size={40} className="text-text-muted" />}
            title="No lead forms yet"
            description="Create one to start capturing leads from Search / Display / Performance Max campaigns."
            action={<Button variant="primary" className="gads-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> New lead form</Button>}
          />
        )}

        {forms && forms.length > 0 && (
          <Card padded={false}>
            <div className="divide-y divide-border">
              {forms.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-5 py-4 hover:bg-surface-alt">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <FormInput size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold text-text-primary">{f.name}</span>
                      {f.callToActionType && (
                        <span className="rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info">{f.callToActionType.replace('_', ' ')}</span>
                      )}
                      {f.delivery && (
                        <span title={DELIVERY[f.delivery].hint} className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs', DELIVERY[f.delivery].tone)}>
                          {f.delivery === 'connected' ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />} {DELIVERY[f.delivery].label}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-text-muted">ID {f.id}</div>
                    {f.headline && <div className="mt-1 text-xs text-text-secondary line-clamp-1"><strong>{f.headline}</strong> · {f.description}</div>}
                    {f.fields && f.fields.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {f.fields.map((field) => (
                          <span key={field} className="rounded-md bg-surface-alt px-1.5 py-0.5 text-2xs text-text-muted">{field}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => { setAttachTarget(f); setAttachCampaignId(campaigns[0]?.id ?? ''); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs text-text-secondary hover:bg-surface-alt"
                      title="Attach to a campaign"
                    >
                      <LinkIcon size={11} /> Attach
                    </button>
                    {f.privacyPolicyUrl && (
                      <a
                        href={f.privacyPolicyUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs text-text-muted hover:bg-surface-alt"
                        title="Privacy policy"
                      >
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Create lead form modal */}
      <Modal
        open={createOpen}
        onClose={() => !busy && setCreateOpen(false)}
        title="Create Google Ads lead form"
        widthClass="max-w-2xl"
      >
        <div className="px-6 py-5 space-y-4">
          <FormField label="Internal name" required hint="What you'll see in this list — not shown to users.">
            <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Lead Form — Q2 promo" autoFocus />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Business name" required hint="Shown to users at the top of the form.">
              <Input value={form.businessName} onChange={(e) => setForm((s) => ({ ...s, businessName: e.target.value }))} placeholder="Acme Stores" />
            </FormField>
            <FormField label="Call-to-action type" required>
              <Select value={form.callToActionType} onChange={(e) => setForm((s) => ({ ...s, callToActionType: e.target.value as GoogleAdsLeadFormCtaType }))}>
                {CTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </FormField>
          </div>

          <FormField label="Headline" required hint="≤30 chars. Big bold line on the ad.">
            <Input value={form.headline} onChange={(e) => setForm((s) => ({ ...s, headline: e.target.value }))} placeholder="Get a free quote in 60s" maxLength={30} />
          </FormField>

          <FormField label="Description" required hint="≤200 chars. Explains what users get.">
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              placeholder="Tell us what you need and our team will follow up within one business day."
              maxLength={200}
            />
          </FormField>

          <FormField label="Privacy policy URL" required>
            <Input
              type="url"
              value={form.privacyPolicyUrl}
              onChange={(e) => setForm((s) => ({ ...s, privacyPolicyUrl: e.target.value }))}
              placeholder="https://your-site.com/privacy"
            />
          </FormField>

          <FormField label="Fields to collect" required hint="Pick the data you want from each lead. Email + Phone is a common minimum.">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {FIELD_OPTIONS.map((opt) => {
                const active = form.fields.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleField(opt.value)}
                    className={clsx(
                      'flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors',
                      active
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border bg-surface text-text-secondary hover:border-primary/40',
                    )}
                  >
                    <span className={active ? 'text-primary' : 'text-text-muted'}>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </FormField>

          <div className="rounded-xl border border-border bg-surface-alt/40 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">After submit</div>
            <FormField label="Headline" required>
              <Input value={form.postSubmitHeadline} onChange={(e) => setForm((s) => ({ ...s, postSubmitHeadline: e.target.value }))} maxLength={30} />
            </FormField>
            <FormField label="Description" required>
              <Input value={form.postSubmitDescription} onChange={(e) => setForm((s) => ({ ...s, postSubmitDescription: e.target.value }))} maxLength={200} />
            </FormField>
            <FormField label="Button text (optional)">
              <Input value={form.postSubmitCallToActionText} onChange={(e) => setForm((s) => ({ ...s, postSubmitCallToActionText: e.target.value }))} placeholder="Visit our site" />
            </FormField>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={create} loading={busy} disabled={busy}>
            <Plus size={14} /> Create lead form
          </Button>
        </div>
      </Modal>

      {/* Attach to campaign modal */}
      <Modal
        open={!!attachTarget}
        onClose={() => !attaching && setAttachTarget(null)}
        title={`Attach "${attachTarget?.name}" to a campaign`}
        widthClass="max-w-md"
      >
        <div className="px-6 py-5 space-y-3">
          {campaigns.length === 0 ? (
            <div className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-xs text-warning">
              No campaigns found. Create one first — Search / Display / Performance Max all support lead forms.
            </div>
          ) : (
            <FormField label="Campaign" required>
              <Select value={attachCampaignId} onChange={(e) => setAttachCampaignId(e.target.value)}>
                <option value="">— Pick a campaign —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.advertisingChannelType ?? 'campaign'})
                  </option>
                ))}
              </Select>
            </FormField>
          )}
          <p className="text-2xs text-text-muted">
            Attaching the same form to multiple campaigns is supported. Detach via Google Ads UI if you want to remove it.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setAttachTarget(null)} disabled={attaching}>Cancel</Button>
          <Button variant="primary" onClick={attach} loading={attaching} disabled={attaching || !attachCampaignId}>
            <LinkIcon size={14} /> Attach
          </Button>
        </div>
      </Modal>

    </>
  );
}
